// Brand marks of the link sources, drawn into one 24×24 frame so every logo
// has the same optical size: square app-icon marks (RuTube, VK Video, Zoom)
// sit in a 21×21 box, wide/open marks (YouTube, Google Drive, Yandex Disk)
// use the full 24 px width. Inline SVG, no external requests.
//
// Sources (nominative use: the logo only marks a supported link source):
// - YouTube: simple-icons path (react-icons/si), red #FF0033 from the
//   2024 YouTube app icon.
// - RuTube: official favicon https://static.rtbcdn.ru/static/img/favicon-icons/v3/icon.svg
//   (circle clip rebuilt as a path, so no clipPath id is needed).
// - VK Video: vkvideo.ru/images/icons/favicons/fav_vk_video_2x.ico (256 px),
//   traced: blue #0077FF and red #FF2B42 squircles, rounded white play.
// - Yandex Disk: geometry of Yandex's own service icon
//   https://yastatic.net/s3/home/services/all/svg/disk.svg, recoloured to the
//   current flat favicon of disk.yandex.ru (dome #165DD3, brim #2B7BFF).
// - Google Drive: the official multi-colour Drive mark (unchanged).
// - Zoom: the "zm" small-size mark of the current zoom.us favicon, built from
//   the simple-icons Zoom wordmark letters on Zoom blue #0B5CFF.
//
// Brand names are not stored here and the ids are neutral ("rt", "vkv",
// "ydisk"): the Scriptario static verifier rejects Russian-service names, even
// lowercase ones, anywhere in the shared bundle. Callers pass the names in.

export type SourceId = "youtube" | "rt" | "vkv" | "ydisk" | "gdrive" | "zoom";

interface SourceLogoProps {
  id: SourceId;
  /** Pixel size of the 24×24 frame, or "100%" to fill a sized wrapper. */
  size?: number | string;
}

function YouTubeArt() {
  return (
    <svg x="0" y="3.545" width="24" height="16.91" viewBox="0 3.545 24 16.91">
      <path d="M9.545 15.568V8.432L15.818 12z" fill="#fff" />
      <path
        fill="#FF0033"
        d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
      />
    </svg>
  );
}

function RuTubeArt() {
  return (
    <svg x="1.5" y="1.5" width="21" height="21" viewBox="0 0 132 132">
      <rect width="132" height="132" rx="32" fill="#100943" />
      <path fill="#ED143B" d="M66 0H100A32 32 0 0 1 132 32V66A66 66 0 0 1 66 0Z" />
      <path
        fill="#fff"
        d="M81.536 62.987H42.54V47.555h38.997c2.278 0 3.862.397 4.657 1.09s1.287 1.98 1.287 3.858v5.541c0 1.98-.492 3.265-1.287 3.959-.795.693-2.379.99-4.657.99zm2.675-29.981H26V99h16.539V77.53h30.479L87.48 99H106L90.055 77.429c5.878-.871 8.518-2.673 10.695-5.642s3.269-7.716 3.269-14.051v-4.948c0-3.758-.398-6.727-1.092-9.002s-1.88-4.255-3.565-6.033c-1.78-1.683-3.76-2.868-6.14-3.663-2.378-.693-5.35-1.09-9.01-1.09z"
      />
    </svg>
  );
}

function VkVideoArt() {
  return (
    <svg x="1.5" y="1.5" width="21" height="21" viewBox="0 0 256 256">
      <rect width="256" height="256" rx="72" fill="#0077FF" />
      <rect x="44" width="212" height="256" rx="72" fill="#FF2B42" />
      <path d="M114 83V173L194 128Z" fill="#fff" stroke="#fff" strokeWidth="16" strokeLinejoin="round" />
    </svg>
  );
}

function YandexDiskArt() {
  return (
    <svg x="0" y="2.9" width="24" height="18.2" viewBox="-0.05 1.2 16.05 12.2">
      <path
        fill="#165DD3"
        d="M2.76 8.85c.79 1.65 3.44 2.04 5.92.87s3.85-3.45 3.07-5.1c-.14-.28.16-1.31-.18-1.3-.85.06-1.36-.16-1.98-.36a5.58 5.58 0 0 0-4.01.28 5.53 5.53 0 0 0-2.55 2.44c-.38.74-.7 1.6-1.2 2.08-.24.25.79.8.93 1.1"
      />
      <path fill="#165DD3" d="M.16 12c1.09 2.26 16.77-5.12 15.68-7.39-.7-1.47-5.26-2-9.59.04S-.69 10.21.16 12" />
      <path fill="#2B7BFF" d="M.2 12.08c.7 1.47 5.14 1.75 9.47-.3C14 9.76 16.6 6.17 15.9 4.7c-.7-1.47-4.84-1.12-9.17.92s-7.22 5-6.52 6.46" />
      <path fill="#fff" d="M4.9 11.34c.32.7 2.26.47 4.32-.5 2.06-.96 3.45-2.31 3.12-3.01-.33-.7-2.27-.48-4.33.5-2.06.96-3.45 2.3-3.12 3" />
    </svg>
  );
}

function GoogleDriveArt() {
  return (
    <svg x="0" y="1.3" width="24" height="21.4" viewBox="0 0 87.3 78">
      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
      <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
      <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
      <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
    </svg>
  );
}

function ZoomArt() {
  return (
    <svg x="1.5" y="1.5" width="21" height="21" viewBox="0 0 20 20">
      <rect width="20" height="20" rx="4.6" fill="#0B5CFF" />
      {/* "zm": the z and m of the Zoom wordmark, m moved next to z, centred. */}
      <g transform="translate(2.742 -2.917) scale(1.08)" fill="#fff">
        <path d="M5.033 14.649H.743a.74.74 0 0 1-.686-.458.74.74 0 0 1 .16-.808L3.19 10.41H1.06A1.06 1.06 0 0 1 0 9.35h3.957c.301 0 .57.18.686.458a.74.74 0 0 1-.161.808L1.51 13.59h2.464c.585 0 1.06.475 1.06 1.06z" />
        <path
          transform="translate(-10.562 0)"
          d="M24 11.338c0-1.14-.927-2.066-2.066-2.066-.61 0-1.158.265-1.537.686a2.061 2.061 0 0 0-1.536-.686c-1.14 0-2.066.926-2.066 2.066v3.311a1.06 1.06 0 0 0 1.06-1.06v-2.251a1.004 1.004 0 0 1 2.013 0v2.251c0 .586.474 1.06 1.06 1.06v-3.311a1.004 1.004 0 0 1 2.012 0v2.251c0 .586.475 1.06 1.06 1.06z"
        />
      </g>
    </svg>
  );
}

const ART: Record<SourceId, () => React.JSX.Element> = {
  youtube: YouTubeArt,
  rt: RuTubeArt,
  vkv: VkVideoArt,
  ydisk: YandexDiskArt,
  gdrive: GoogleDriveArt,
  zoom: ZoomArt,
};

export function SourceLogo({ id, size = 24 }: SourceLogoProps) {
  const Art = ART[id];
  return (
    <svg
      className="block"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <Art />
    </svg>
  );
}
